import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  getFirestore,
} from 'firebase/firestore';

const ERROR_DB_NAME = `${import.meta.env.VITE_ERROR_DB_PREFIX}-${import.meta.env.VITE_LOCATION_SHORTHAND}`;
const ERRORS_COLLECTION = 'patients';

function normalizeErrorFilterValue(value) {
  return String(value || '').trim();
}

function buildErrorFilterConfig({ userIdFilter, userIdFilterOperator, patientIdFilter, patientIdFilterOperator, apiFilter, apiFilterOperator }) {
  const normalizedUserIdFilter = normalizeErrorFilterValue(userIdFilter);
  const normalizedPatientIdFilter = normalizeErrorFilterValue(patientIdFilter);
  const normalizedApiFilter = normalizeErrorFilterValue(apiFilter);

  if (normalizedUserIdFilter) {
    return {
      fieldName: 'user_id',
      filterValue: normalizedUserIdFilter,
      filterOperator: userIdFilterOperator === 'equals' ? 'equals' : 'startsWith',
    };
  }

  if (normalizedPatientIdFilter) {
    return {
      fieldName: 'patient_id',
      filterValue: normalizedPatientIdFilter,
      filterOperator: patientIdFilterOperator === 'equals' ? 'equals' : 'startsWith',
    };
  }

  if (normalizedApiFilter) {
    return {
      fieldName: 'api',
      filterValue: normalizedApiFilter,
      filterOperator: apiFilterOperator === 'equals' ? 'equals' : 'startsWith',
    };
  }

  return null;
}

function mapErrorDoc(docSnap) {
  const data = docSnap.data() || {};

  return {
    id: docSnap.id,
    userId: data.user_id || '',
    patientId: data.patient_id || '',
    api: data.api || '',
    error: data.error || '',
  };
}

export async function getError(id) {
  if (!id) throw new Error('getError: missing id');

  const db = getFirestore(ERROR_DB_NAME);
  const errorRef = doc(db, ERRORS_COLLECTION, id);
  const snapshot = await getDoc(errorRef);

  return snapshot.exists() ? mapErrorDoc(snapshot) : null;
}

export async function listErrors({
  pageSize = 10,
  cursorSnapshot = null,
  userIdFilter = '',
  userIdFilterOperator = 'startsWith',
  patientIdFilter = '',
  patientIdFilterOperator = 'startsWith',
  apiFilter = '',
  apiFilterOperator = 'startsWith',
} = {}) {
  const db = getFirestore(ERROR_DB_NAME);
  const errorsRef = collection(db, ERRORS_COLLECTION);
  const filterConfig = buildErrorFilterConfig({
    userIdFilter,
    userIdFilterOperator,
    patientIdFilter,
    patientIdFilterOperator,
    apiFilter,
    apiFilterOperator,
  });

  if (filterConfig) {
    const { fieldName, filterValue, filterOperator } = filterConfig;
    const filterConstraints = filterOperator === 'equals'
      ? [where(fieldName, '==', filterValue)]
      : [
          where(fieldName, '>=', filterValue),
          where(fieldName, '<=', filterValue + '\uf8ff'),
        ];

    const filteredQuery = query(
      errorsRef,
      ...filterConstraints,
      orderBy(fieldName, 'asc'),
      ...(cursorSnapshot ? [startAfter(cursorSnapshot)] : []),
      limit(pageSize + 1),
    );

    const snapshot = await getDocs(filteredQuery);
    const hasMore = snapshot.docs.length > pageSize;
    const pageDocs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs;
    const errors = pageDocs.map(mapErrorDoc);
    const lastVisibleSnapshot = pageDocs.length ? pageDocs[pageDocs.length - 1] : null;

    return { errors, totalCount: null, lastVisibleSnapshot, hasMore };
  }

  const listQuery = query(
    errorsRef,
    orderBy(documentId(), 'asc'),
    ...(cursorSnapshot ? [startAfter(cursorSnapshot)] : []),
    limit(pageSize + 1),
  );

  const snapshot = await getDocs(listQuery);
  const hasMore = snapshot.docs.length > pageSize;
  const pageDocs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs;
  const errors = pageDocs.map(mapErrorDoc);
  const lastVisibleSnapshot = pageDocs.length ? pageDocs[pageDocs.length - 1] : null;

  return { errors, totalCount: null, lastVisibleSnapshot, hasMore };
}